// 학생 출결 현황 페이지

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useStudentAuth } from '../hooks/useStudentAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const ITEMS_PER_PAGE = 7;

interface AttendanceRecord {
  id: string;
  date: string;
  check_in_time: string | null;
  check_out_time: string | null;
  status: 'present' | 'absent' | 'late';
}

export default function AttendancePage() {
  const { student } = useStudentAuth();
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    fetchAttendance(0, true);
  }, [student]);

  const fetchAttendance = async (currentOffset: number, isInitial: boolean = false) => {
    if (!student?.id) return;

    try {
      if (isInitial) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const token = localStorage.getItem('student_access_token');

      const response = await fetch(
        `${API_BASE_URL}/api/v1/attendance/student/${student.id}/recent?offset=${currentOffset}&limit=${ITEMS_PER_PAGE}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const newRecords = data.records || [];

        if (isInitial) {
          setRecords(newRecords);
        } else {
          setRecords((prev) => [...prev, ...newRecords]);
        }

        setHasMore(data.has_more || false);
        setOffset(currentOffset + ITEMS_PER_PAGE);
      }
    } catch (error) {
      console.error('출결 조회 에러:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    fetchAttendance(offset, false);
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'present':
        return <Chip label="출석" color="success" size="small" />;
      case 'late':
        return <Chip label="지각" color="warning" size="small" />;
      case 'absent':
        return <Chip label="결석" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        출결 현황
      </Typography>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          {isLoading ? (
            <Typography>로딩 중...</Typography>
          ) : records.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                출결 기록이 없습니다.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>날짜</TableCell>
                      <TableCell>입실</TableCell>
                      <TableCell>퇴실</TableCell>
                      <TableCell>상태</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{record.date}</TableCell>
                        <TableCell>{record.check_in_time || '-'}</TableCell>
                        <TableCell>{record.check_out_time || '-'}</TableCell>
                        <TableCell>{getStatusChip(record.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* 더보기 버튼 */}
              {hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    startIcon={isLoadingMore ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
                  >
                    {isLoadingMore ? '불러오는 중...' : '이전 기록 더보기'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
